import { ChangeEvent, useCallback } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { getDayText, getHoursRangeText } from '@app/i18n';
import { Theme } from '@app/theme';
import { useTrack } from '@app/analytics';
import { InformationBox } from '@app/core';

import { ErrorMsg } from '../ErrorMsg';
import { useOptionalField } from '../FormikFields';
import { createFutureDayMap } from './createFutureDayMap';
import { RadioBlock } from '../RadioBlock';
import { AdminDateSelection } from './AdminDateSelection';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    maxWidth: '100%',
    marginRight: -theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h5,
    marginTop: theme.marginBase * 3,
    marginBottom: theme.marginBase * 3,
    '&:first-letter': {
      textTransform: 'capitalize',
    },
  },
  hoursLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
}));

interface Props {
  duration?: number;
  dates: Date[];
  name: string;
  dayClassName?: string;
  timezone?: string;
  trackingName?: string;
}

export const DateSelectionField = ({
  duration,
  dates,
  name,
  dayClassName,
  timezone,
  trackingName,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { i18n, t } = useTranslation('common');

  const { field } = useOptionalField({ name });
  const dayHoursMap = createFutureDayMap(dates, undefined, timezone);
  const track = useTrack();

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (trackingName) {
        track(`${trackingName} Selected`, {
          label: e.currentTarget.value,
        });
      }
    },
    [trackingName, track],
  );

  if (!dates.length) {
    return (
      <div className={classes.container}>
        <InformationBox variant="danger" text={t('form.noDates')} />
        <AdminDateSelection name={name} />
      </div>
    );
  }

  return (
    <>
      <div className={classes.container}>
        {Object.keys(dayHoursMap)
          .sort()
          .map((day: string) => (
            <div key={day}>
              <AdminDateSelection name={name} />
              <h3 className={classnames(classes.title, dayClassName)}>
                {getDayText(day, i18n.language, timezone)}
              </h3>

              <div className={classes.hoursLine}>
                {dayHoursMap[day].map((date) => (
                  <RadioBlock
                    label={getHoursRangeText(
                      date,
                      duration,
                      i18n.language,
                      timezone,
                    )}
                    placeholder={getHoursRangeText(
                      '2000-01-01 10:00',
                      45,
                      i18n.language,
                    )}
                    key={date.toISOString()}
                    value={date.toISOString()}
                    name={name}
                    selected={date.toISOString() === field.value}
                    onChange={onChange}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>
      <ErrorMsg name={name} />
    </>
  );
};
